import type { ReactNode } from "react";
import styles from "./Heading.module.css";

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;
type HeadingSize = "hero" | "h2" | "h3" | "h4";
type HeadingColor = "primary" | "inherit" | "on-dark" | "emerald";

interface HeadingProps {
  readonly level: HeadingLevel;
  readonly size?: HeadingSize;
  readonly color?: HeadingColor;
  readonly children: ReactNode;
  readonly className?: string;
}

const SIZE_MAP = {
  hero: {
    fontSize: "var(--type-hero-size)",
    lineHeight: "var(--type-hero-line-height)",
    letterSpacing: "var(--type-hero-letter-spacing)",
  },
  h2: {
    fontSize: "var(--type-h2-size)",
    lineHeight: "var(--type-h2-line-height)",
    letterSpacing: "var(--type-h2-letter-spacing)",
  },
  h3: {
    fontSize: "var(--type-h3-size)",
    lineHeight: "var(--type-h3-line-height)",
    letterSpacing: "var(--type-h3-letter-spacing)",
  },
  h4: {
    fontSize: "var(--type-h4-size)",
    lineHeight: "var(--type-h4-line-height)",
    letterSpacing: "0",
  },
} as const;

const COLOR_MAP = {
  primary: "var(--color-text-primary)",
  inherit: "inherit",
  "on-dark": "var(--color-text-on-dark)",
  emerald: "var(--color-accent-solid)",
} as const;

export function Heading({
  level,
  size,
  color = "primary",
  children,
  className,
}: HeadingProps) {
  const Element = `h${level}` as const;
  const resolvedSize = size ?? (level === 1 ? "hero" : level === 2 ? "h2" : level === 3 ? "h3" : "h4");
  const sizeStyles = SIZE_MAP[resolvedSize];

  return (
    <Element
      className={`${styles.heading} ${className ?? ""}`}
      style={{
        fontSize: sizeStyles.fontSize,
        lineHeight: sizeStyles.lineHeight,
        letterSpacing: sizeStyles.letterSpacing,
        color: COLOR_MAP[color],
      }}
    >
      {children}
    </Element>
  );
}
